import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useScope, useFetch } from "@/lib/useScope";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, UsersRound, Trash2, Phone, Mail, Pencil, Clock } from "lucide-react";
import { COUNTRY_CODES } from "@/lib/countryCodes";
import { Switch } from "@/components/ui/switch";

const ROLES = ["Chairman", "Secretary", "Treasurer", "Vice Chairman", "Joint Secretary", "Society Manager", "Member"];
const ROLE_COLOR = {
  Chairman: ["#eff6ff", "#2563eb", "#bfdbfe"],
  Secretary: ["#f5f3ff", "#7c3aed", "#ddd6fe"],
  Treasurer: ["#ecfdf5", "#059669", "#a7f3d0"],
  "Society Manager": ["#fff7ed", "#ea580c", "#fed7aa"],
};
const emptyForm = { name: "", role: "Member", tower: "", dial: "91", phone: "", email: "", priority: "", acceptance_time_hours: 2, in_escalation: true };

export default function Committee() {
  const cfg = useScope();
  const { data, loading, reload } = useFetch("/society/committee");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const members = data || [];
  const hasManager = members.some((m) => m.role === "Society Manager");

  const defaultPriority = (role, withManager) => {
    if (role === "Society Manager") return 1;
    if (role === "Secretary") return withManager ? 2 : 1;
    return "";
  };
  const effPriority = (m) => {
    if (m.in_escalation === false) return null;
    if (m.priority != null && m.priority > 0) return m.priority;
    if (m.role === "Society Manager") return 1;
    if (m.role === "Secretary") return hasManager ? 2 : 1;
    return null;
  };

  const onRole = (role) =>
    setForm((f) => ({ ...f, role, priority: role === "Member" ? "" : (f.priority === "" ? defaultPriority(role, hasManager) : f.priority) }));

  const openAdd = () => { setEditingId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (m) => {
    let dial = "91", number = m.phone || "";
    const match = COUNTRY_CODES.map((c) => c.dial).filter((d) => number.startsWith(d)).sort((a, b) => b.length - a.length)[0];
    if (match) { dial = match; number = number.slice(match.length); }
    setEditingId(m.id);
    setForm({ name: m.name || "", role: m.role || "Member", tower: m.tower || "", dial, phone: number, email: m.email || "", priority: m.priority ?? "", acceptance_time_hours: m.acceptance_time_hours ?? 2, in_escalation: m.in_escalation !== false });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      name: form.name, role: form.role, tower: form.tower, email: form.email,
      phone: form.phone ? form.dial + form.phone.replace(/\D/g, "") : "",
      priority: form.role === "Member" ? null : (form.priority === "" ? null : Number(form.priority)),
      acceptance_time_hours: Number(form.acceptance_time_hours) || 2,
      in_escalation: form.in_escalation,
    };
    try {
      if (editingId) await api.put(`/society/committee/${editingId}`, payload, cfg);
      else await api.post("/society/committee", payload, cfg);
      toast.success(editingId ? "Member updated" : "Member added");
      setOpen(false); setForm(emptyForm); setEditingId(null); reload();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const del = async (m) => { try { await api.delete(`/society/committee/${m.id}`); reload(); toast.success("Removed"); } catch (e) { toast.error(apiError(e)); } };
  const roleColor = (r) => ROLE_COLOR[r] || ["#f1f5f9", "#475569", "#e2e8f0"];
  const showPriority = form.role !== "Member" && form.in_escalation;

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="committee-page">
      <PageHeader title="Society Committee" subtitle="Managing Committee" description="Manage committee members used for escalation alerts">
        <Button data-testid="add-committee-btn" onClick={openAdd}><Plus className="mr-1.5 h-4 w-4" />Add Member</Button>
      </PageHeader>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(emptyForm); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-cabinet">{editingId ? "Edit Committee Member" : "New Committee Member"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input data-testid="committee-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Role</Label>
              <Select value={form.role} onValueChange={onRole}><SelectTrigger data-testid="committee-role-select" className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="flex items-start justify-between gap-3 rounded-lg border border-border bg-slate-50 p-3">
              <div>
                <Label className="text-xs font-semibold">Part of escalation matrix</Label>
                <p className="mt-0.5 text-[10px] text-zinc-500">When off, this person never gets tickets assigned, escalations or alerts.</p>
              </div>
              <Switch data-testid="committee-escalation-switch" checked={form.in_escalation} onCheckedChange={(v) => setForm({ ...form, in_escalation: v })} />
            </div>
            {showPriority && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Escalation priority</Label>
                  <Input type="number" min="1" data-testid="committee-priority-input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="mt-1" placeholder="e.g. 1" />
                  <p className="mt-1 text-[10px] text-zinc-400">Lower = contacted first. Blank keeps this role out of the chain.</p>
                </div>
                <div>
                  <Label className="text-xs">Acceptance time (hours)</Label>
                  <Input type="number" min="0" step="0.5" data-testid="committee-acceptance-input" value={form.acceptance_time_hours} onChange={(e) => setForm({ ...form, acceptance_time_hours: e.target.value })} className="mt-1" placeholder="2" />
                  <p className="mt-1 text-[10px] text-zinc-400">If not accepted in time, it escalates to the next member. Default 2h.</p>
                </div>
              </div>
            )}
            <div><Label className="text-xs">Tower / Block</Label><Input data-testid="committee-tower-input" value={form.tower} onChange={(e) => setForm({ ...form, tower: e.target.value })} className="mt-1" placeholder="Tower A" /></div>
            <div><Label className="text-xs">Phone</Label>
              <div className="mt-1 flex gap-2">
                <Select value={form.dial} onValueChange={(v) => setForm({ ...form, dial: v })}>
                  <SelectTrigger data-testid="committee-country-code" className="w-[150px] shrink-0"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">{COUNTRY_CODES.map((c) => <SelectItem key={c.dial + c.label} value={c.dial}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid="committee-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="flex-1" placeholder="9000000001" />
              </div>
            </div>
            <div><Label className="text-xs">Email</Label><Input data-testid="committee-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button data-testid="save-committee-btn" onClick={save} disabled={saving || !form.name}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editingId ? "Save Changes" : "Add Member"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {loading ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> :
        members.length === 0 ? <div className="rounded-lg border border-dashed p-12 text-center text-sm text-slate-400"><UsersRound className="mx-auto mb-3 h-8 w-8 text-slate-300" />No committee members yet.</div> : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {members.map((m) => {
              const [bg, fg, br] = roleColor(m.role);
              const pr = effPriority(m);
              return (
                <div key={m.id} data-testid={`committee-card-${m.id}`} className="card-hover rounded-xl border border-border bg-white p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-full text-[16px] font-black" style={{ background: bg, color: fg }}>{(m.name || "?").charAt(0).toUpperCase()}</div>
                      <div>
                        <div className="text-[15px] font-bold text-slate-900">{m.name}</div>
                        <span className="mt-0.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: bg, color: fg, border: `1px solid ${br}` }}>{m.role}</span>
                        <span data-testid={`committee-escalation-badge-${m.id}`} className={`ml-1.5 mt-0.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${m.in_escalation === false ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-700"}`}>{m.in_escalation === false ? "Not in escalation" : "In escalation"}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button data-testid={`edit-committee-${m.id}`} onClick={() => openEdit(m)} className="text-slate-300 hover:text-brand"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`delete-committee-${m.id}`} onClick={() => del(m)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <div className="mt-4 space-y-1.5 border-t border-border pt-3 text-[12px] text-slate-500">
                    {pr != null && (
                      <div className="flex items-center gap-1.5 font-semibold text-slate-700">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand/10 text-[10px] font-bold text-brand">{pr}</span>
                        Escalation priority
                      </div>
                    )}
                    {pr != null && <div className="flex items-center gap-1.5"><Clock className="h-3 w-3" />Responds within {m.acceptance_time_hours ?? 2}h</div>}
                    {m.tower && <div>🏢 {m.tower}</div>}
                    {m.phone && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3" />{m.phone}</div>}
                    {m.email && <div className="flex items-center gap-1.5"><Mail className="h-3 w-3" />{m.email}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
